import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import apiClient, { Evidence, DemoUser } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { EvidenceViewerModal } from '../components/evidence/EvidenceViewerModal';
import { LegalHoldManager } from '../components/case/LegalHoldManager';
import {
  ArrowLeft,
  Folder,
  FileBox,
  History,
  Download,
  Shield,
  ShieldCheck,
  ShieldX,
  CheckCircle,
  AlertTriangle,
  FileText,
  Image,
  Video,
  File,
  Scale,
  Loader2,
  Eye,
  X,
  Upload,
  Share2,
  Check,
  Gavel,
  Clock,
  CheckCircle2,
  XCircle,
  Sparkles,
  Users,
  Save,
} from 'lucide-react';
import { useState } from 'react';
import { format } from 'date-fns';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

function getFileIcon(fileType: string) {
  if (fileType.startsWith('image/')) return Image;
  if (fileType.startsWith('video/')) return Video;
  if (fileType.includes('pdf') || fileType.includes('document')) return FileText;
  return File;
}

export default function CaseDetail() {
  const { caseNumber } = useParams<{ caseNumber: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedEvidence, setSelectedEvidence] = useState<Evidence | null>(null);
  const [downloadConfirm, setDownloadConfirm] = useState<Evidence | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [verificationResults, setVerificationResults] = useState<Record<string, 'verified' | 'failed' | 'pending'>>({});
  const [legalHoldEvidence, setLegalHoldEvidence] = useState<Evidence | null>(null);
  
  // Share Evidence state
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedToShare, setSelectedToShare] = useState<Set<string>>(new Set());
  const [shareNotes, setShareNotes] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'evidence' | 'filings' | 'team'>('evidence');

  const isPublicSubmitter = user?.role === 'PublicSubmitter';

  const { data: caseData, isLoading: caseLoading } = useQuery({
    queryKey: ['case', caseNumber],
    queryFn: () => apiClient.getCase(caseNumber!),
    enabled: !!caseNumber,
  });

  // Check if user is defense attorney (need case data first)
  const isDefenseAttorney = caseData && user && caseData.defenseAttorney === user.email;

  // Evidence query - defense attorney only sees shared evidence
  const { data: evidenceResult, isLoading: evidenceLoading } = useQuery({
    queryKey: ['case-evidence', caseNumber, isDefenseAttorney ? 'discovery' : 'full'],
    queryFn: async () => {
      if (isDefenseAttorney) {
        // Defense attorney - use discovery endpoint that returns only shared items
        const result = await apiClient.getCaseEvidenceForDiscovery(caseNumber!);
        return result.evidence;
      }
      // Everyone else sees all evidence
      return apiClient.getCaseEvidence(caseNumber!);
    },
    enabled: !!caseNumber && caseData !== undefined,
  });
  const evidence = evidenceResult;

  // Court filings query
  const { data: filings, isLoading: filingsLoading } = useQuery({
    queryKey: ['case-filings', caseNumber],
    queryFn: () => apiClient.getCaseFilings(caseNumber!),
    enabled: !!caseNumber,
  });

  // Download with confirmation and custody logging
  const downloadMutation = useMutation({
    mutationFn: async (item: Evidence) => {
      const { url, filename } = await apiClient.downloadEvidence(item.id);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      return item;
    },
    onSuccess: () => {
      setDownloadConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['custody-events'] });
    },
  });

  // Integrity verification
  const verifyMutation = useMutation({
    mutationFn: async (evidenceId: string) => {
      setVerifyingId(evidenceId);
      // Simulate integrity check (would call actual API)
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // In real implementation: await apiClient.verifyIntegrity(evidenceId);
      return { evidenceId, status: 'verified' as const };
    },
    onSuccess: (result) => {
      setVerificationResults((prev) => ({
        ...prev,
        [result.evidenceId]: result.status,
      }));
      setVerifyingId(null);
    },
    onError: (_, evidenceId) => {
      setVerificationResults((prev) => ({
        ...prev,
        [evidenceId]: 'failed',
      }));
      setVerifyingId(null);
    },
  });

  // Share evidence with defense
  const shareMutation = useMutation({
    mutationFn: async () => {
      if (!caseNumber || selectedToShare.size === 0) {
        throw new Error('No items selected');
      }
      return apiClient.shareEvidenceWithDefense(caseNumber, Array.from(selectedToShare), shareNotes || undefined);
    },
    onSuccess: (result) => {
      setShowShareModal(false);
      setSelectedToShare(new Set());
      setShareNotes('');
      queryClient.invalidateQueries({ queryKey: ['discovery-shares', caseNumber] });
      alert(`Successfully shared ${result.sharedCount} item(s) with defense (${result.defenseAttorney})`);
    },
    onError: (error: any) => {
      alert(error?.response?.data?.error || 'Failed to share evidence');
    },
  });

  // Check if user is prosecution attorney on this case
  const isProsecutionAttorney = caseData?.prosecutingAttorney === user?.email;
  const hasDefenseAttorney = !!caseData?.defenseAttorney;
  const isCaseManager = user?.role === 'CaseManager' || user?.role === 'SystemAdmin';

  // Team management - fetch all users for dropdown (CaseManager only)
  const { data: allUsers } = useQuery({
    queryKey: ['demo-users'],
    queryFn: () => apiClient.getDemoUsers(),
    enabled: isCaseManager && activeTab === 'team',
  });

  // Team assignment state
  const [teamForm, setTeamForm] = useState<{
    assignedJudge: string;
    leadInvestigator: string;
    prosecutingAttorney: string;
    defenseAttorney: string;
  } | null>(null);

  // Initialize team form when case data loads
  const initTeamForm = () => {
    if (caseData) {
      setTeamForm({
        assignedJudge: caseData.assignedJudge || '',
        leadInvestigator: caseData.leadInvestigator || '',
        prosecutingAttorney: caseData.prosecutingAttorney || '',
        defenseAttorney: caseData.defenseAttorney || '',
      });
    }
  };

  const teamMutation = useMutation({
    mutationFn: async (team: { assignedJudge?: string | null; leadInvestigator?: string | null; prosecutingAttorney?: string | null; defenseAttorney?: string | null }) => {
      return apiClient.updateCaseTeam(caseNumber!, team);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', caseNumber] });
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });

  if (caseLoading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="text-gray-500 mt-4">Loading case...</p>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Case not found</p>
        <Link
          to="/cases"
          className="text-primary-600 hover:text-primary-700 mt-4 inline-block"
        >
          Return to cases
        </Link>
      </div>
    );
  }

  const getIntegrityIcon = (item: Evidence) => {
    const status = verificationResults[item.id] || item.integrityStatus;
    switch (status) {
      case 'verified':
        return <ShieldCheck className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <ShieldX className="h-5 w-5 text-red-600" />;
      default:
        return <Shield className="h-5 w-5 text-gray-400" />;
    }
  };

  const verifiedCount = evidence?.filter(
    (e) => (verificationResults[e.id] || e.integrityStatus) === 'verified'
  ).length ?? 0;

  return (
    <div className="space-y-6">
      {/* Back link */}
      <Link
        to="/cases"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to cases
      </Link>

      {/* Case header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Folder className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{caseData.caseNumber}</h1>
              <p className="text-gray-600 mt-1">{caseData.title}</p>
              {caseData.description && (
                <p className="text-gray-500 text-sm mt-2">{caseData.description}</p>
              )}
            </div>
          </div>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              caseData.status === 'open' || caseData.status === 'Active'
                ? 'bg-green-100 text-green-800'
                : caseData.status === 'pending' || caseData.status === 'Pending Review'
                ? 'bg-amber-100 text-amber-800'
                : caseData.status === 'closed'
                ? 'bg-gray-100 text-gray-800'
                : 'bg-blue-100 text-blue-800'
            }`}
          >
            {caseData.status}
          </span>
        </div>

        {/* Case metadata */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
          {caseData.jurisdiction && (
            <div>
              <p className="text-sm text-gray-500">Jurisdiction</p>
              <p className="font-medium text-gray-900">{caseData.jurisdiction}</p>
            </div>
          )}
          {caseData.court && (
            <div>
              <p className="text-sm text-gray-500">Court</p>
              <p className="font-medium text-gray-900">{caseData.court}</p>
            </div>
          )}
          {caseData.assignedJudge && (
            <div>
              <p className="text-sm text-gray-500">Assigned Judge</p>
              <p className="font-medium text-gray-900">{caseData.assignedJudgeName || caseData.assignedJudge}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500">Created</p>
            <p className="font-medium text-gray-900">
              {format(new Date(caseData.createdAt), 'MMM d, yyyy')}
            </p>
          </div>
        </div>
      </div>

      {/* Integrity summary bar */}
      {evidence && evidence.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-gray-900">
                Evidence Integrity
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span className="text-green-600">
                <CheckCircle className="h-4 w-4 inline mr-1" />
                {verifiedCount} verified
              </span>
              {evidence.length - verifiedCount > 0 && (
                <span className="text-gray-500">
                  {evidence.length - verifiedCount} pending
                </span>
              )}
              <button
                onClick={() => {
                  evidence.forEach((e) => {
                    if (!verificationResults[e.id] && e.integrityStatus !== 'verified') {
                      verifyMutation.mutate(e.id);
                    }
                  });
                }}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Verify All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6" aria-label="Case sections">
          <button
            onClick={() => setActiveTab('evidence')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'evidence'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileBox className="h-4 w-4 inline mr-2" />
            Evidence ({evidence?.length ?? 0})
          </button>
          <button
            onClick={() => setActiveTab('filings')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'filings'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Gavel className="h-4 w-4 inline mr-2" />
            Court Filings ({filings?.length ?? 0})
          </button>
          {isCaseManager && (
            <button
              onClick={() => { setActiveTab('team'); initTeamForm(); }}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'team'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="h-4 w-4 inline mr-2" />
              Case Team
            </button>
          )}
        </nav>
      </div>

      {/* Evidence Tab */}
      {activeTab === 'evidence' && (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FileBox className="h-5 w-5 text-gray-400" />
            Evidence ({evidence?.length ?? 0})
          </h2>
          <div className="flex items-center gap-2">
            {/* Share Evidence button - for prosecution attorney */}
            {isProsecutionAttorney && hasDefenseAttorney && evidence && evidence.length > 0 && (
              <button
                onClick={() => setShowShareModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Share2 className="h-4 w-4" />
                Share Evidence
              </button>
            )}
            {isPublicSubmitter && (
              <Link
                to={`/submit?case=${caseNumber}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Upload className="h-4 w-4" />
                Upload Evidence
              </Link>
            )}
          </div>
        </div>

        {/* Defense attorney notice */}
        {isDefenseAttorney && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            You are viewing evidence shared with you through discovery by the prosecution.
          </div>
        )}

        {evidenceLoading ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          </div>
        ) : evidence?.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <FileBox className="h-12 w-12 text-gray-400 mx-auto" />
            <p className="text-gray-500 mt-4">
              {isDefenseAttorney ? 'No evidence has been shared with you yet' : 'No evidence files'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
            {evidence?.map((item) => {
              const FileIcon = getFileIcon(item.fileType);
              const isVerifying = verifyingId === item.id;
              const status = verificationResults[item.id] || item.integrityStatus;

              return (
                <div key={item.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="p-2 bg-gray-100 rounded-lg shrink-0">
                        <FileIcon className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="min-w-0">
                        <button
                          onClick={() => setSelectedEvidence(item)}
                          className="font-medium text-gray-900 hover:text-primary-600 truncate block text-left"
                        >
                          {item.fileName}
                        </button>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(item.fileSize)} • {item.evidenceType}
                          {item.uploadedAt && (
                            <> • {format(new Date(item.uploadedAt), 'MMM d, yyyy')}</>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {/* AI indexed badge */}
                      {item.AiIndexedAt && (
                        <span 
                          title={`AI Indexed: ${new Date(item.AiIndexedAt).toLocaleString()}`}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-violet-100 text-violet-800"
                        >
                          <Sparkles className="h-3 w-3 mr-1" />
                          AI Ready
                        </span>
                      )}

                      {/* Legal hold badge */}
                      {item.legalHold && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-800">
                          <Scale className="h-3 w-3 mr-1" />
                          Legal Hold
                        </span>
                      )}

                      {/* Integrity status */}
                      <span
                        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
                          status === 'verified'
                            ? 'bg-green-100 text-green-800'
                            : status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {status}
                      </span>

                      {/* View button */}
                      <button
                        onClick={() => setSelectedEvidence(item)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="View evidence"
                      >
                        <Eye className="h-5 w-5" />
                      </button>

                      {/* Verify integrity button */}
                      <button
                        onClick={() => verifyMutation.mutate(item.id)}
                        disabled={isVerifying}
                        className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                        title="Verify integrity"
                      >
                        {isVerifying ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          getIntegrityIcon(item)
                        )}
                      </button>

                      {/* Legal hold button */}
                      <button
                        onClick={() => setLegalHoldEvidence(item)}
                        className="p-2 text-gray-400 hover:text-amber-600 rounded-lg hover:bg-gray-100"
                        title="Manage legal hold"
                      >
                        <Scale className="h-5 w-5" />
                      </button>

                      {/* Custody chain link */}
                      <Link
                        to={`/custody/${item.id}`}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="View custody chain"
                      >
                        <History className="h-5 w-5" />
                      </Link>

                      {/* Download button */}
                      <button
                        onClick={() => setDownloadConfirm(item)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                        title="Download"
                      >
                        <Download className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  {item.description && (
                    <p className="mt-2 text-sm text-gray-500 ml-14">{item.description}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Court Filings Tab */}
      {activeTab === 'filings' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Gavel className="h-5 w-5 text-gray-400" />
              Court Filings ({filings?.length ?? 0})
            </h2>
            <Link
              to={`/court-filing?case=${caseNumber}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              <FileText className="h-4 w-4" />
              Submit Filing
            </Link>
          </div>

          {filingsLoading ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : filings?.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Gavel className="h-12 w-12 text-gray-400 mx-auto" />
              <p className="text-gray-500 mt-4">No court filings yet</p>
              <Link
                to={`/court-filing?case=${caseNumber}`}
                className="inline-flex items-center gap-2 mt-4 text-primary-600 hover:text-primary-700 font-medium"
              >
                Submit the first filing
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
              {filings?.map((filing) => (
                <div key={filing.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="p-2 bg-gray-100 rounded-lg shrink-0">
                        <FileText className="h-5 w-5 text-gray-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-gray-900 truncate">{filing.title}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                          <span>{filing.filingType}</span>
                          <span>•</span>
                          <span>Filed by {filing.filedBy.split('@')[0]}</span>
                          <span>•</span>
                          <span>{format(new Date(filing.filedAt), 'MMM d, yyyy h:mm a')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full ${
                          filing.status === 'Accepted'
                            ? 'bg-green-100 text-green-800'
                            : filing.status === 'Rejected'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {filing.status === 'Accepted' && <CheckCircle2 className="h-3 w-3" />}
                        {filing.status === 'Rejected' && <XCircle className="h-3 w-3" />}
                        {filing.status === 'Pending' && <Clock className="h-3 w-3" />}
                        {filing.status}
                      </span>
                    </div>
                  </div>
                  {filing.description && (
                    <p className="mt-2 text-sm text-gray-500 ml-14">{filing.description}</p>
                  )}
                  {filing.reviewedBy && (
                    <p className="mt-2 text-xs text-gray-400 ml-14">
                      Reviewed by {filing.reviewedBy.split('@')[0]} on {format(new Date(filing.reviewedAt!), 'MMM d, yyyy')}
                      {filing.reviewNotes && ` - "${filing.reviewNotes}"`}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Team Tab - CaseManager only */}
      {activeTab === 'team' && isCaseManager && teamForm && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="h-5 w-5 text-gray-400" />
                Case Team Assignments
              </h2>
              <p className="text-sm text-gray-500 mt-1">Assign personnel to this case by role</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Assigned Judge */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Judge</label>
              <select
                value={teamForm.assignedJudge}
                onChange={(e) => setTeamForm({ ...teamForm, assignedJudge: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="">— Unassigned —</option>
                {allUsers?.filter((u: Omit<DemoUser, 'pin'>) => u.role === 'CaseManager' || (u as any).role === 'Judge').map((u: Omit<DemoUser, 'pin'>) => (
                  <option key={u.id} value={u.email}>{u.name}</option>
                ))}
              </select>
              {teamForm.assignedJudge && (
                <p className="text-xs text-gray-400 mt-1">{teamForm.assignedJudge}</p>
              )}
            </div>

            {/* Lead Investigator */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lead Investigator</label>
              <select
                value={teamForm.leadInvestigator}
                onChange={(e) => setTeamForm({ ...teamForm, leadInvestigator: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="">— Unassigned —</option>
                {allUsers?.filter((u: Omit<DemoUser, 'pin'>) => u.role === 'LawEnforcement').map((u: Omit<DemoUser, 'pin'>) => (
                  <option key={u.id} value={u.email}>{u.name}</option>
                ))}
              </select>
              {teamForm.leadInvestigator && (
                <p className="text-xs text-gray-400 mt-1">{teamForm.leadInvestigator}</p>
              )}
            </div>

            {/* Prosecuting Attorney */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prosecuting Attorney</label>
              <select
                value={teamForm.prosecutingAttorney}
                onChange={(e) => setTeamForm({ ...teamForm, prosecutingAttorney: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="">— Unassigned —</option>
                {allUsers?.filter((u: Omit<DemoUser, 'pin'>) => u.role === 'Attorney').map((u: Omit<DemoUser, 'pin'>) => (
                  <option key={u.id} value={u.email}>{u.name}</option>
                ))}
              </select>
              {teamForm.prosecutingAttorney && (
                <p className="text-xs text-gray-400 mt-1">{teamForm.prosecutingAttorney}</p>
              )}
            </div>

            {/* Defense Attorney */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Defense Attorney</label>
              <select
                value={teamForm.defenseAttorney}
                onChange={(e) => setTeamForm({ ...teamForm, defenseAttorney: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="">— Unassigned —</option>
                {allUsers?.filter((u: Omit<DemoUser, 'pin'>) => u.role === 'Attorney').map((u: Omit<DemoUser, 'pin'>) => (
                  <option key={u.id} value={u.email}>{u.name}</option>
                ))}
              </select>
              {teamForm.defenseAttorney && (
                <p className="text-xs text-gray-400 mt-1">{teamForm.defenseAttorney}</p>
              )}
            </div>
          </div>

          {/* Save button */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
            {teamMutation.isSuccess && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                Team updated successfully
              </span>
            )}
            {teamMutation.isError && (
              <span className="text-sm text-red-600">Failed to update team</span>
            )}
            <button
              onClick={() => {
                teamMutation.mutate({
                  assignedJudge: teamForm.assignedJudge || null,
                  leadInvestigator: teamForm.leadInvestigator || null,
                  prosecutingAttorney: teamForm.prosecutingAttorney || null,
                  defenseAttorney: teamForm.defenseAttorney || null,
                });
              }}
              disabled={teamMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {teamMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Assignments
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Download Confirmation Dialog */}
      {downloadConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-full shrink-0">
                <Download className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  Download Evidence
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  You are about to download:
                </p>
                <p className="font-medium text-gray-900 mt-2">{downloadConfirm.fileName}</p>
              </div>
            </div>

            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Chain of Custody Notice</p>
                  <p className="text-amber-700 mt-1">
                    This download will be logged in the custody chain with your user ID,
                    timestamp, and IP address for audit purposes.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setDownloadConfirm(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => downloadMutation.mutate(downloadConfirm)}
                disabled={downloadMutation.isPending}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
              >
                {downloadMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Confirm Download
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Evidence Viewer Modal */}
      <EvidenceViewerModal
        isOpen={selectedEvidence !== null}
        evidenceId={selectedEvidence?.id ?? null}
        onClose={() => setSelectedEvidence(null)}
      />

      {/* Legal Hold Manager Modal */}
      {legalHoldEvidence && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Legal Hold Manager</h2>
              <button
                onClick={() => setLegalHoldEvidence(null)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <LegalHoldManager
                evidenceId={legalHoldEvidence.id}
                onHoldApplied={() => {
                  queryClient.invalidateQueries({ queryKey: ['case-evidence', caseNumber] });
                }}
                onHoldReleased={() => {
                  queryClient.invalidateQueries({ queryKey: ['case-evidence', caseNumber] });
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Share Evidence Modal */}
      {showShareModal && evidence && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Share Evidence with Defense</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Select items to share with {caseData?.defenseAttorneyName || caseData?.defenseAttorney || 'defense attorney'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setSelectedToShare(new Set());
                  setShareNotes('');
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              {/* Select all toggle */}
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-200">
                <input
                  type="checkbox"
                  id="select-all"
                  checked={selectedToShare.size === evidence.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedToShare(new Set(evidence.map(e => e.id)));
                    } else {
                      setSelectedToShare(new Set());
                    }
                  }}
                  className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                />
                <label htmlFor="select-all" className="text-sm font-medium text-gray-700">
                  Select All ({evidence.length} items)
                </label>
              </div>

              {/* Evidence list */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {evidence.map((item) => {
                  const FileIcon = getFileIcon(item.fileType);
                  const isSelected = selectedToShare.has(item.id);
                  return (
                    <label
                      key={item.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const next = new Set(selectedToShare);
                          if (e.target.checked) {
                            next.add(item.id);
                          } else {
                            next.delete(item.id);
                          }
                          setSelectedToShare(next);
                        }}
                        className="h-4 w-4 text-indigo-600 rounded border-gray-300"
                      />
                      <div className="p-2 bg-gray-100 rounded">
                        <FileIcon className="h-4 w-4 text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{item.fileName}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(item.fileSize)} • {item.evidenceType}</p>
                      </div>
                      {isSelected && <Check className="h-5 w-5 text-indigo-600" />}
                    </label>
                  );
                })}
              </div>

              {/* Notes */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  value={shareNotes}
                  onChange={(e) => setShareNotes(e.target.value)}
                  placeholder="Add notes for the defense attorney..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  rows={2}
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  {selectedToShare.size} item(s) selected
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setShowShareModal(false);
                      setSelectedToShare(new Set());
                      setShareNotes('');
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => shareMutation.mutate()}
                    disabled={selectedToShare.size === 0 || shareMutation.isPending}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {shareMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sharing...
                      </>
                    ) : (
                      <>
                        <Share2 className="h-4 w-4" />
                        Share with Defense
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}